import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PageContainer,
  PageHeader,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Button,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@homy/ui";
import {
  Workflow,
  RefreshCw,
  Play,
  Pause,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Activity,
  Calendar,
  AlertCircle,
  Timer,
} from "lucide-react";
import {
  getTemporalSummary,
  getTemporalSchedules,
  triggerTemporalSchedule,
  pauseTemporalSchedule,
} from "../lib/api/temporalClient";

function ExecutionStatusBadge({ status }) {
  const s = (status || "SCHEDULED").toUpperCase();
  switch (s) {
    case "RUNNING":
      return (
        <Badge
          variant="outline"
          className="border-indigo-500/40 bg-indigo-500/15 text-indigo-400 font-mono text-[11px] gap-1"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-indigo-400 animate-pulse" />
          RUNNING
        </Badge>
      );
    case "COMPLETED":
      return (
        <Badge
          variant="outline"
          className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400 font-mono text-[11px] gap-1"
        >
          <CheckCircle2 className="h-3 w-3 text-emerald-400" />
          COMPLETED
        </Badge>
      );
    case "FAILED":
      return (
        <Badge
          variant="outline"
          className="border-rose-500/40 bg-rose-500/15 text-rose-400 font-mono text-[11px] gap-1"
        >
          <AlertTriangle className="h-3 w-3 text-rose-400" />
          FAILED
        </Badge>
      );
    case "TIMED_OUT":
      return (
        <Badge
          variant="outline"
          className="border-amber-500/40 bg-amber-500/15 text-amber-400 font-mono text-[11px] gap-1"
        >
          <Clock className="h-3 w-3 text-amber-400" />
          TIMED_OUT
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="font-mono text-[11px]">
          {s}
        </Badge>
      );
  }
}

export function TemporalJobsPage() {
  const queryClient = useQueryClient();
  const [triggeringId, setTriggeringId] = useState(null);
  const [pausingId, setPausingId] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);

  // Queries
  const {
    data: summary,
    isLoading: summaryLoading,
    refetch: refetchSummary,
  } = useQuery({
    queryKey: ["platform-temporal-summary"],
    queryFn: getTemporalSummary,
    refetchInterval: 15000,
  });

  const {
    data: schedulesData,
    isLoading: schedulesLoading,
    isFetching,
    refetch: refetchSchedules,
  } = useQuery({
    queryKey: ["platform-temporal-schedules"],
    queryFn: getTemporalSchedules,
    refetchInterval: 15000,
  });

  // Mutations
  const triggerMutation = useMutation({
    mutationFn: (id) => triggerTemporalSchedule(id),
    onMutate: (id) => {
      setTriggeringId(id);
      setStatusMessage(null);
    },
    onSuccess: (res) => {
      setStatusMessage({ type: "success", text: res.message || "Workflow execution triggered" });
      queryClient.invalidateQueries({ queryKey: ["platform-temporal-schedules"] });
      queryClient.invalidateQueries({ queryKey: ["platform-temporal-summary"] });
    },
    onError: (err) => {
      setStatusMessage({
        type: "error",
        text: err?.response?.data?.detail || "Failed to trigger workflow",
      });
    },
    onSettled: () => {
      setTriggeringId(null);
    },
  });

  const pauseMutation = useMutation({
    mutationFn: (id) => pauseTemporalSchedule(id),
    onMutate: (id) => {
      setPausingId(id);
      setStatusMessage(null);
    },
    onSuccess: (res) => {
      setStatusMessage({
        type: "success",
        text: res.paused ? "Schedule paused" : "Schedule resumed",
      });
      queryClient.invalidateQueries({ queryKey: ["platform-temporal-schedules"] });
      queryClient.invalidateQueries({ queryKey: ["platform-temporal-summary"] });
    },
    onError: (err) => {
      setStatusMessage({
        type: "error",
        text: err?.response?.data?.detail || "Failed to toggle pause state",
      });
    },
    onSettled: () => {
      setPausingId(null);
    },
  });

  const handleRefresh = () => {
    refetchSummary();
    refetchSchedules();
  };

  const schedules = schedulesData?.schedules || [];
  const isConnected = summary?.status === "CONNECTED";
  const clusterHealth = summary?.clusterHealth || (isConnected ? "healthy" : "idle");

  return (
    <PageContainer className="max-w-7xl mx-auto space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Temporal Jobs & Workflows"
        description="Sovereign orchestration console for recurring cadences, background queues, and deterministic workflow engines."
        actions={
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={handleRefresh}
            disabled={isFetching}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      {/* Cluster Health Banner if Disconnected or Idle */}
      {!isConnected && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 flex items-center justify-between text-xs text-amber-200">
          <div className="flex items-center gap-2.5">
            <AlertCircle className="h-4 w-4 text-amber-400 shrink-0" />
            <div>
              <span className="font-semibold text-amber-300">
                Temporal Cluster Operating in Offline / Idle Mode
              </span>
              <p className="text-amber-200/80 text-[11px] mt-0.5">
                Local cluster gRPC port 7233 is connecting or idle. All recurring cadences and scheduled definitions are operating in graceful simulated standby.
              </p>
            </div>
          </div>
          <Badge variant="outline" className="border-amber-500/40 bg-amber-500/20 text-amber-300 font-mono">
            {summary?.status || "DISCONNECTED"}
          </Badge>
        </div>
      )}

      {/* Status feedback message */}
      {statusMessage && (
        <div
          className={`rounded-xl border p-3 text-xs flex items-center justify-between ${
            statusMessage.type === "success"
              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
              : "border-rose-500/30 bg-rose-500/10 text-rose-300"
          }`}
        >
          <span>{statusMessage.text}</span>
          <button
            type="button"
            onClick={() => setStatusMessage(null)}
            className="text-muted-foreground hover:text-foreground text-xs"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Summary KPI Cards Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Cluster Status */}
        <Card className="border-border">
          <CardContent className="p-4 space-y-1">
            <p className="text-[10px] uppercase font-mono tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
              <Activity className="h-3.5 w-3.5 text-primary" /> CLUSTER STATUS
            </p>
            <div className="flex items-center gap-2">
              <span
                className={`h-2.5 w-2.5 rounded-full ${
                  isConnected ? "bg-emerald-500 animate-pulse" : "bg-amber-400"
                }`}
              />
              <span className="text-xl font-bold font-mono text-foreground">
                {summaryLoading ? "..." : summary?.status || "STANDBY"}
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Health: <span className="font-mono text-foreground capitalize">{clusterHealth}</span>
            </p>
          </CardContent>
        </Card>

        {/* Active Schedules */}
        <Card className="border-border">
          <CardContent className="p-4 space-y-1">
            <p className="text-[10px] uppercase font-mono tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5 text-indigo-400" /> ACTIVE SCHEDULES
            </p>
            <p className="text-2xl font-bold font-mono text-foreground">
              {summaryLoading ? "..." : summary?.activeSchedulesCount ?? schedules.length}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {schedules.filter((s) => s.paused).length} paused
            </p>
          </CardContent>
        </Card>

        {/* Failure Rate */}
        <Card className="border-border">
          <CardContent className="p-4 space-y-1">
            <p className="text-[10px] uppercase font-mono tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 text-rose-400" /> FAILURE RATE
            </p>
            <p className="text-2xl font-bold font-mono text-foreground">
              {summaryLoading ? "..." : `${((summary?.failureRate || 0) * 100).toFixed(1)}%`}
            </p>
            <p className="text-[11px] text-muted-foreground">Historical run retry metrics</p>
          </CardContent>
        </Card>

        {/* Task Queue */}
        <Card className="border-border">
          <CardContent className="p-4 space-y-1">
            <p className="text-[10px] uppercase font-mono tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
              <Timer className="h-3.5 w-3.5 text-sky-400" /> WORKER QUEUE
            </p>
            <p className="text-lg font-bold font-mono text-sky-400 truncate">
              homy-tasks
            </p>
            <p className="text-[11px] text-muted-foreground">Sandboxed Python worker</p>
          </CardContent>
        </Card>
      </div>

      {/* Scheduled Workflows Table */}
      <Card className="border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Workflow className="h-4 w-4 text-primary" /> Scheduled Cadence Workflows
          </CardTitle>
        </CardHeader>
        <CardContent>
          {schedulesLoading ? (
            <div className="p-12 text-center text-sm text-muted-foreground flex flex-col items-center justify-center gap-2">
              <RefreshCw className="h-6 w-6 animate-spin text-primary" />
              <p>Connecting to Temporal schedule registry...</p>
            </div>
          ) : schedules.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground space-y-2">
              <Workflow className="h-8 w-8 mx-auto text-muted-foreground/60" />
              <p className="font-medium text-foreground">No Recurring Workflows Registered</p>
              <p className="text-xs">No active cadence schedules currently detected in this namespace.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Workflow & Description</TableHead>
                    <TableHead>Cron Cadence</TableHead>
                    <TableHead>Next Run Time</TableHead>
                    <TableHead>Last Execution</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {schedules.map((schedule) => {
                    const isTriggering = triggeringId === schedule.id;
                    const isPausing = pausingId === schedule.id;

                    return (
                      <TableRow key={schedule.id} className="hover:bg-muted/30">
                        <TableCell className="space-y-1 max-w-sm">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm text-foreground">
                              {schedule.workflowType}
                            </span>
                            {schedule.paused && (
                              <Badge
                                variant="outline"
                                className="border-amber-500/40 bg-amber-500/10 text-amber-400 text-[10px] font-mono"
                              >
                                PAUSED
                              </Badge>
                            )}
                          </div>
                          {schedule.description && (
                            <p className="text-xs text-muted-foreground line-clamp-1">
                              {schedule.description}
                            </p>
                          )}
                          <p className="text-[10px] font-mono text-muted-foreground/80">
                            ID: {schedule.id}
                          </p>
                        </TableCell>

                        <TableCell>
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-muted/50 border border-border text-xs font-mono text-foreground">
                            <Clock className="h-3 w-3 text-muted-foreground" />
                            {schedule.cron}
                          </span>
                        </TableCell>

                        <TableCell>
                          <span className="text-xs font-mono text-muted-foreground">
                            {schedule.nextRun
                              ? new Date(schedule.nextRun).toLocaleString()
                              : "—"}
                          </span>
                        </TableCell>

                        <TableCell className="space-y-0.5">
                          <p className="text-xs font-mono text-muted-foreground">
                            {schedule.lastRunAt
                              ? new Date(schedule.lastRunAt).toLocaleTimeString()
                              : "—"}
                          </p>
                          {schedule.failureCount > 0 && (
                            <span className="text-[10px] font-mono text-rose-400">
                              {schedule.failureCount} retries
                            </span>
                          )}
                        </TableCell>

                        <TableCell>
                          <ExecutionStatusBadge status={schedule.lastState} />
                        </TableCell>

                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {/* Trigger Ad-Hoc */}
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 text-xs gap-1 hover:text-primary hover:border-primary/50"
                              onClick={() => triggerMutation.mutate(schedule.id)}
                              disabled={isTriggering || schedule.paused}
                              title="Manually trigger workflow execution"
                            >
                              <Play className={`h-3 w-3 ${isTriggering ? "animate-spin" : ""}`} />
                              Trigger
                            </Button>

                            {/* Pause / Resume */}
                            <Button
                              variant="ghost"
                              size="sm"
                              className={`h-8 text-xs gap-1 ${
                                schedule.paused
                                  ? "text-emerald-400 hover:text-emerald-300"
                                  : "text-muted-foreground hover:text-foreground"
                              }`}
                              onClick={() => pauseMutation.mutate(schedule.id)}
                              disabled={isPausing}
                              title={schedule.paused ? "Resume schedule" : "Pause schedule"}
                            >
                              <Pause className="h-3 w-3" />
                              {schedule.paused ? "Resume" : "Pause"}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}

export default TemporalJobsPage;
